import type { UIMatch } from 'react-router';
import { api } from './api';

/**
 * Cookieless visit counting (see apps/api/src/analytics). The browser sends the route pattern
 * (e.g. /people/:id, never the id), where the first page of a visit came from, and nothing else.
 * Browsers that ask not to be tracked (Global Privacy Control, Do Not Track) send nothing.
 */
const FIRST_TOUCH_KEY = 'mx-first-touch';

function optedOut(): boolean {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.globalPrivacyControl === true || navigator.doNotTrack === '1';
}

/** /people/abc123 with params { id: 'abc123' } becomes /people/:id. */
export function routePattern(pathname: string, matches: UIMatch[]): string {
  const params = matches.at(-1)?.params ?? {};
  let path = pathname;
  for (const [key, value] of Object.entries(params)) {
    if (value && key !== 'step' && key !== 'section') path = path.replace(`/${value}`, `/:${key}`);
  }
  return path;
}

let firstPage = true;

export function trackPageView(path: string) {
  if (optedOut() || path.startsWith('/admin')) return;
  const params = new URLSearchParams(window.location.search);
  const source = params.get('utm_source') ?? undefined;
  const referrer = firstPage && document.referrer ? document.referrer : undefined;
  if (firstPage) {
    firstPage = false;
    try {
      // Where this visit came from, kept in this tab only, in case it ends with a sign-up.
      if (!sessionStorage.getItem(FIRST_TOUCH_KEY) && (source || referrer)) {
        sessionStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify({ source, referrer }));
      }
    } catch {
      // storage can be unavailable (private mode); counting still works
    }
  }
  void fetch('/api/beacon', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    keepalive: true,
    body: JSON.stringify({ path, referrer, source }),
  }).catch(() => undefined);
}

/** After sign-up: tells the server where this visit came from, then forgets it. */
export function sendAttributionOnce() {
  try {
    const raw = sessionStorage.getItem(FIRST_TOUCH_KEY);
    sessionStorage.removeItem(FIRST_TOUCH_KEY);
    if (!raw || optedOut()) return;
    const { source, referrer } = JSON.parse(raw) as { source?: string; referrer?: string };
    void api.api.v1.me.attribution
      .$post({ json: { source: source?.slice(0, 60), referrer: referrer?.slice(0, 500) } })
      .catch(() => undefined);
  } catch {
    // never block the app over analytics
  }
}
